import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { LineChart } from 'react-native-chart-kit';

const { width } = Dimensions.get('window');

const SSYCalculatorScreen = () => {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isWeb = Platform.OS === 'web';
  const isMobile = screenData.width < 900;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  const [formData, setFormData] = useState({
    yearlyInvestment: '60000',
    interestRate: '7.6',
    depositYears: '15',
    totalYears: '21',
    childAge: '1',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateSSY = () => {
    const yearlyInvestment = parseFloat(formData.yearlyInvestment);
    const rate = parseFloat(formData.interestRate) / 100;
    const depositYears = parseFloat(formData.depositYears);
    const totalYears = parseFloat(formData.totalYears);
    const childAge = parseFloat(formData.childAge);

    if ([yearlyInvestment, rate, depositYears, totalYears, childAge].some(v => isNaN(v))) {
      setResults(null);
      return;
    }

    let balance = 0;
    const yearlySeries = [];
    for (let year = 1; year <= totalYears; year++) {
      if (year <= depositYears) balance += yearlyInvestment; // yearly deposit at start of year
      balance = balance * (1 + rate); // annual compounding
      yearlySeries.push(balance);
    }

    const investedAmount = yearlyInvestment * depositYears;
    const expectedReturns = balance - investedAmount;
    const maturityAge = childAge + totalYears;

    setResults({
      investedAmount: Math.round(investedAmount),
      expectedReturns: Math.round(expectedReturns),
      totalValue: Math.round(balance),
      maturityAge,
      yearlyInvestment,
      interestRate: parseFloat(formData.interestRate),
      depositYears,
      totalYears,
      yearlySeries,
    });
  };

  useEffect(() => {
    calculateSSY();
  }, [formData]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>SSY Calculator</Text>
        <Text style={styles.heroSubtitle}>
          Estimate Sukanya Samriddhi Yojana maturity value, returns and investment summary
        </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
        <View style={[styles.formContent, isMobile && { flex: 1 }]}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Yearly SSY Investment</Text>
            <Text style={styles.helperText}>Min ₹250, Max ₹1,50,000 per financial year</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput
                style={styles.input}
                value={formData.yearlyInvestment}
                onChangeText={(v) => handleInputChange('yearlyInvestment', v)}
                keyboardType="numeric"
                placeholder="60000"
                placeholderTextColor={colors.textSecondary}
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Interest Rate (p.a.)</Text>
            <View style={styles.inputContainer}>
              <TextInput
                style={styles.input}
                value={formData.interestRate}
                onChangeText={(v) => handleInputChange('interestRate', v)}
                keyboardType="numeric"
                placeholder="7.6"
                placeholderTextColor={colors.textSecondary}
              />
              <Text style={styles.percentSymbol}>%</Text>
            </View>
            <Text style={styles.helperText}>Govt. announced quarterly; calculator assumes constant rate for projection</Text>
          </View>

          <View style={[styles.inputRow, isMobile && { flexDirection: 'column', gap: 0 }]}>
            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Deposit Years</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.depositYears}
                  onChangeText={(v) => handleInputChange('depositYears', v)}
                  keyboardType="numeric"
                  placeholder="15"
                  placeholderTextColor={colors.textSecondary}
                />
                <Text style={styles.suffixText}>Years</Text>
              </View>
              <Text style={styles.helperText}>Deposits allowed for up to 15 years</Text>
            </View>

            <View style={[styles.inputGroup, styles.half, isMobile && { marginBottom: 16 }]}>
              <Text style={styles.label}>Total Tenure</Text>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={formData.totalYears}
                  onChangeText={(v) => handleInputChange('totalYears', v)}
                  keyboardType="numeric"
                  placeholder="21"
                  placeholderTextColor={colors.textSecondary}
                />
                <Text style={styles.suffixText}>Years</Text>
              </View>
              <Text style={styles.helperText}>Account matures after 21 years from opening</Text>
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Child Age at Account Opening</Text>
            <View style={styles.inputContainer}>
              <TextInput
                style={styles.input}
                value={formData.childAge}
                onChangeText={(v) => handleInputChange('childAge', v)}
                keyboardType="numeric"
                placeholder="1"
                placeholderTextColor={colors.textSecondary}
              />
              <Text style={styles.suffixText}>Years</Text>
            </View>
          </View>
        </View>

        <View style={[styles.summaryPanel, isMobile && { flex: 1, marginTop: 16 }]}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Investment Summary</Text>
            <Text style={styles.panelSubtitle}>SSY Investment Results</Text>
          </View>

          {results ? (
            <View style={styles.resultsContainer}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Expected Returns</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.expectedReturns)}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Invested Amount</Text>
                <Text style={styles.summaryValue}>{formatCurrency(results.investedAmount)}</Text>
              </View>
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Total Value</Text>
                <Text style={styles.totalValue}>{formatCurrency(results.totalValue)}</Text>
              </View>
              <View style={styles.divider} />
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Maturity Age</Text>
                <Text style={styles.summaryValue}>{formatNumber(results.maturityAge)} years</Text>
              </View>
              {renderSSYChart()}
            </View>
          ) : (
            <Text style={styles.placeholderText}>Enter valid inputs to view results</Text>
          )}
        </View>
      </View>
    </View>
  );

  const renderSSYChart = () => {
    if (!results?.yearlySeries?.length) return null;
    const labels = results.yearlySeries.map((_, i) => {
      const year = i + 1;
      return (year === 1 || year === results.depositYears || year === results.totalYears) ? String(year) : '';
    });
    // Keep chart comfortably inside summary panel
    const chartWidth = isMobile
      ? Math.min(screenData.width - 40, 320)
      : 320;
    return (
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Yearly Balance Projection</Text>
        <LineChart
          data={{ labels, datasets: [{ data: results.yearlySeries.map(v => Math.round(v)) }] }}
          width={chartWidth}
          height={220}
          withDots={false}
          withInnerLines={false}
          chartConfig={{
            backgroundColor: '#ffffff',
            backgroundGradientFrom: '#ffffff',
            backgroundGradientTo: '#ffffff',
            decimalPlaces: 0,
            color: (opacity=1) => `rgba(16, 185, 129, ${opacity})`,
            labelColor: (opacity=1) => `rgba(100, 116, 139, ${opacity})`,
          }}
          style={{ borderRadius: 8 }}
        />
      </View>
    );
  };

  const renderSSYInformation = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>What is SSY (Sukanya Samriddhi Yojana)?</Text>
      <Text style={styles.sectionText}>
        Sukanya Samriddhi Yojana is a government-backed savings scheme for the girl child.
        It offers attractive returns with guaranteed safety and significant tax benefits under Section 80C.
        Deposits are permitted for 15 years and the account matures after 21 years from account opening.
      </Text>

      <Text style={styles.sectionSubtitle}>Key Benefits</Text>
      <View style={styles.featuresList}>
        <Text style={styles.featureItem}>• Government guaranteed returns with annual compounding</Text>
        <Text style={styles.featureItem}>• Tax deduction up to ₹1.5 lakh under Section 80C</Text>
        <Text style={styles.featureItem}>• Highest safety for long-term child education planning</Text>
        <Text style={styles.featureItem}>• Flexible yearly deposits with minimum ₹250</Text>
      </View>

      <Text style={styles.sectionSubtitle}>How this calculator works</Text>
      <Text style={styles.sectionText}>
        We simulate year-by-year deposits and annual compounding at the selected rate.
        Deposits are assumed at the start of each year for up to 15 years, and interest accrues
        for the full 21-year tenure. Actual government-notified rates change quarterly and
        may cause slight variances in real-world outcomes.
      </Text>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>SSY Calculator: Complete Guide</Text>
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Eligibility & Account Rules</Text>
        <Text style={styles.seoText}>Account can be opened for a girl child up to 10 years of age. One account per child, up to two children (exceptions for twins).</Text>
        <Text style={styles.seoText}>Deposits allowed for 15 years; account matures after 21 years from opening. Premature closures are restricted.</Text>
      </View>
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Deposits, Interest & Tax Benefits</Text>
        <Text style={styles.seoText}>Minimum ₹250 and maximum ₹1,50,000 per financial year. Interest is compounded annually; government revises rates quarterly.</Text>
        <Text style={styles.seoText}>Deposits qualify for deduction under Section 80C. Interest is tax-free; maturity proceeds are exempt.</Text>
      </View>
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Plan Better with Related Tools</Text>
        <View style={styles.linksRow}>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SIPCalculator')}>
            <Text style={styles.linkText}>SIP Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('PPFCalculator')}>
            <Text style={styles.linkText}>PPF Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('PFCalculator')}>
            <Text style={styles.linkText}>EPF Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('FDCalculator')}>
            <Text style={styles.linkText}>FD Calculator</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('IncomeTaxCalculator')}>
            <Text style={styles.linkText}>Income Tax Calculator</Text>
          </TouchableOpacity>
        </View>
      </View>
      <Text style={styles.disclaimer}>Rates and rules may change; consult official notifications for current SSY rates.</Text>
    </View>
  );

  const renderFAQSection = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons name={showFAQ ? 'chevron-up' : 'chevron-down'} size={24} color={colors.primary} />
      </TouchableOpacity>
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What are the deposit limits?</Text>
            <Text style={styles.faqAnswer}>Minimum ₹250 and maximum ₹1,50,000 per financial year.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>When does the account mature?</Text>
            <Text style={styles.faqAnswer}>After 21 years from account opening. Deposits stop after 15 years.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is the interest rate fixed?</Text>
            <Text style={styles.faqAnswer}>The government revises rates quarterly. This calculator assumes a constant rate for projections.</Text>
          </View>
        </View>
      )}
    </View>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      {renderCalculator()}
      {renderSSYInformation()}
      {renderSEOContent()}
      {renderFAQSection()}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  heroSection: {
    backgroundColor: '#0ea5e9',
    paddingVertical: 24,
    paddingHorizontal: 30,
  },
  heroContent: {
    width: '100%',
    alignSelf: 'stretch',
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 6,
  },
  heroSubtitle: {
    fontSize: 14,
    color: '#e0f2fe',
  },
  calculatorContainer: {
    width: '100%',
    alignSelf: 'stretch',
    paddingHorizontal: 30,
    paddingVertical: 20,
  },
  calculatorRow: {
    flexDirection: 'row',
    gap: 24,
  },
  formContent: {
    gap: 20,
    flex: 0.7,
  },
  summaryPanel: {
    flex: 0.3,
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 20,
    height: 'fit-content',
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  panelHeader: {
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  panelTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
  },
  panelSubtitle: {
    fontSize: 12,
    color: '#64748b',
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputRow: {
    flexDirection: 'row',
    gap: 16,
  },
  half: {
    flex: 1,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  helperText: {
    fontSize: 12,
    color: '#64748b',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    paddingHorizontal: 12,
    height: 48,
  },
  currencySymbol: {
    fontSize: 16,
    color: '#64748b',
    marginRight: 8,
  },
  percentSymbol: {
    fontSize: 16,
    color: '#64748b',
    marginLeft: 8,
  },
  suffixText: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 8,
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
    borderWidth: 0,
  },
  resultsContainer: {
    gap: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: 14,
    color: '#334155',
  },
  summaryValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  divider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: 8,
  },
  totalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#065f46',
  },
  placeholderText: {
    fontSize: 14,
    color: '#64748b',
  },
  infoSection: {
    marginVertical: 20,
    backgroundColor: '#ffffff',
    padding: 20,
    marginHorizontal: 30,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
  },
  sectionSubtitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  sectionText: {
    fontSize: 16,
    color: '#475569',
    lineHeight: 24,
    marginBottom: 12,
  },
  featuresList: {
    marginLeft: 8,
    marginBottom: 12,
  },
  featureItem: {
    fontSize: 16,
    color: '#475569',
    lineHeight: 24,
    marginBottom: 6,
  },
  faqContainer: {
    marginHorizontal: 0,
    marginBottom: 20,
      marginHorizontal: 30,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    backgroundColor: '#0ea5e910',
    borderRadius: 12,
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  faqContent: {
    backgroundColor: '#ffffff',
    padding: 20,
    borderRadius: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  faqItem: {
    marginBottom: 16,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 6,
  },
  faqAnswer: {
    fontSize: 15,
    color: '#475569',
    lineHeight: 22,
  },
  chartSection: {
    marginTop: 12,
    alignItems: 'center',
  },
  chartTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  seoContainer: {
    marginHorizontal: 0,
    marginBottom: 20,
    backgroundColor: '#ffffff',
    padding: 20,
    marginHorizontal: 30,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  seoTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
    textAlign: 'center',
  },
  seoSection: {
    marginTop: 8,
    marginBottom: 16,
  },
  seoSubtitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  seoText: {
    fontSize: 16,
    color: '#475569',
    lineHeight: 24,
    marginBottom: 8,
  },
  linksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  linkButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#0ea5e910',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  linkText: {
    color: '#0ea5e9',
    fontWeight: '600',
  },
  disclaimer: {
    fontSize: 12,
    color: '#64748b',
    marginTop: 4,
  },
});

export default SSYCalculatorScreen;